import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import notifications from "../../../../common/Components/Notifications/Notification";
import { useCashbookStatus } from "../../../../helpers/cashbookStatus";
import { getCashDay } from "../../../../services/cashBookApi";
import { cashReturn } from "../../../../services/karkhanaApi";
import { confirmUnsavedLogout } from "../../daily/confirmUnsavedLogout";
import { useKT } from "../../i18n/kt";
import { kp } from "../../karkhanaPaths";
import { newRequestId } from "../../rounds/entryQueue";
import { rupees } from "../../rates/rateModel";
import { logoutThisPhone } from "../../supervisor/supervisorSession";
import { readRole } from "../CashbookGuard";
import { fmtShortDate, fmtTime, istToday, num, pageList, refusalText, sumLine } from "../money";
import { removeQueuedPhoto, startPhotoQueue, useFailedPhotos, usePendingPhotos } from "../photoQueue";
import TransferCards from "../transfer/TransferCards";
import type { CashDay, DayPrimary, EntryView, MissingItem, SentItem, SupervisorState } from "../types";
import Keypad from "../ui/Keypad";
import "../cashbook.scss";

const readAuth = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users || {};
  } catch {
    return {};
  }
};

const ICONS = {
  printer: (
    <>
      <path d="M7 9V3h10v6" />
      <rect x="3" y="9" width="18" height="8" rx="2" />
      <path d="M7 14h10v7H7z" />
    </>
  ),
  sign: (
    <>
      <circle cx="8" cy="8" r="4.5" />
      <path d="M6.5 6.5h3M6.5 8h3M7.4 6.5c1.4 0 1.4 1.5 0 1.5l2 2.3" />
      <path d="M3 20c2.5-2 4-2 5.5 0s3 2 5 0 3.5-2 7.5-1" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
      <circle cx="12" cy="13.5" r="3.8" />
    </>
  ),
  give: (
    <>
      <path d="M3 13h4l3 2h4a1.5 1.5 0 0 1 0 3h-4" />
      <path d="M7 19l2 1h7l5-4a1.5 1.5 0 0 0-2-2.2L16 16" />
      <circle cx="15" cy="6.5" r="3.5" />
    </>
  ),
  back: (
    <>
      <path d="M9 14L4 9l5-5" />
      <path d="M4 9h11a5 5 0 0 1 0 10h-3" />
    </>
  ),
  spend: (
    <>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6M9 12h6" />
    </>
  ),
};

const Svg = ({ name }: { name: keyof typeof ICONS }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {ICONS[name]}
  </svg>
);

/** The supervisor's words for a record's state (never audit words, UX_COPY terms). */
export const STATE_WORD: Record<SupervisorState, string> = { SENT: "Sent ✓", CHECKED: "Checked ✓", CHANGE_PENDING: "Change asked", VOID: "Removed" };

/** The oldest missing item of a kind: a page sent the next morning counts for the day it was missing. */
const oldestMissing = (day: CashDay, kind: MissingItem["kind"]): MissingItem | undefined =>
  day.missing.filter((m) => m.kind === kind).sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))[0];

/** Where the main button goes, and what it says (V1_SPEC 8.2 `primary`). The camera gets the missing day. */
export const primaryAction = (day: CashDay): { label: string; to: string } | null => {
  const sheet = oldestMissing(day, "PAY_SHEET");
  const spends = oldestMissing(day, "SPENDS");
  const map: Record<DayPrimary, { label: string; to: string } | null> = {
    PRINT_SHEET: { label: "Print pay sheet", to: kp.paySheets() },
    SEND_SHEET_PHOTO: {
      label: "Send pay sheet photo",
      to: kp.cashCamera({ kind: "PAY_SHEET", printId: sheet?.printId || null, page: sheet?.pagesMissing?.[0] ?? null, date: sheet?.date || null }),
    },
    SEND_SPENDS_PHOTO: { label: "Send spends page photo", to: kp.cashCamera({ kind: "SPENDS", date: spends?.date || null }) },
    NONE: null,
  };
  return map[day.primary] ?? null;
};

/** yyyy-MM-dd moved by whole days. */
export const shiftDay = (iso: string, by: number): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + by);
  return d.toISOString().slice(0, 10);
};

/** The evening / next-morning reminder card's line (V1_SPEC 7.3, UX_COPY frame 3). */
export const reminderText = (missing: MissingItem[], today: string): string | null => {
  const sheet = missing.find((m) => m.kind === "PAY_SHEET");
  if (sheet) return sheet.date < today ? "Yesterday's pay sheet isn't sent yet. Send the photo now." : "Today's pay sheet isn't sent yet. Send a photo when you finish.";
  if (missing.some((m) => m.kind === "SPENDS")) return "Today's spends page isn't sent yet. Send a photo when you finish.";
  return null;
};

const entryTitle = (e: EntryView, t: (s: string, v?: any) => string): string => {
  if (e.kind === "EXPENSE") return [e.headName, e.paidTo].filter(Boolean).join(" · ") || t("Spend");
  if (e.kind === "HANDOVER") return t("Cash given to you");
  if (e.kind === "RETURN") return t("Cash given back");
  if (e.kind === "ADVANCE") return [t("Advance"), e.personName].filter(Boolean).join(" · ");
  return e.personName || t("Paid");
};

const entryState = (e: EntryView): SupervisorState =>
  e.status !== "ACTIVE" ? "VOID" : e.pendingChange ? "CHANGE_PENDING" : e.verifyState === "VERIFIED" ? "CHECKED" : "SENT";

type Props = { holderUserId?: string; readOnly?: boolean };

/** A queued photo the server refused for good: what it says, and where Retake goes. */
const FailedPhotos = () => {
  const { t, lang } = useKT();
  const failed = useFailedPhotos();
  if (!failed.length) return null;
  return (
    <div className="cb-card is-warn" role="alert">
      {failed.map((f) => {
        const what = f.meta.kind === "SPENDS" ? t("Spends page · {{date}}", { date: fmtShortDate(f.meta.date, lang) }) : t("Pay sheet photo · {{date}}", { date: fmtShortDate(f.meta.date, lang) });
        // A superseded print's page goes again as the live print (the camera finds it from the QR).
        const retake =
          f.meta.kind === "SPENDS"
            ? kp.cashCamera({ kind: "SPENDS", date: f.meta.date })
            : kp.cashCamera({ kind: "PAY_SHEET", printId: f.failedCode === "PRINT_SUPERSEDED" ? null : f.meta.printId, page: f.failedCode === "PRINT_SUPERSEDED" ? null : f.meta.page, date: f.meta.date });
        return (
          <div key={f.clientRequestId} className="cb-failed">
            <p>
              <strong>{t("Photo not sent: {{what}}", { what })}</strong>
              <br />
              {refusalText({ code: f.failedCode || "", message: f.failedMessage || "" }, t)}
            </p>
            <div className="cb-row-btns">
              <Link to={retake} className="cb-btn is-small">
                {t("Retake")}
              </Link>
              <button type="button" className="cb-btn is-small" onClick={() => removeQueuedPhoto(f.clientRequestId)}>
                {t("Remove")}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * नकद: the supervisor's own cash book home (V1_SPEC 11.4 A1, A2, A6, A8). One big number, the line that
 * adds up to it, the one thing to do next, and what he sent. The owner's HolderDay shows the same screen
 * read-only for one supervisor.
 */
const SupervisorCashHome = ({ holderUserId, readOnly = false }: Props) => {
  const { t, lang } = useKT();
  const history = useHistory();
  const { pathname, search } = useLocation();
  const status = useCashbookStatus();
  const pending = usePendingPhotos();
  const auth = readAuth();
  const [day, setDay] = useState<CashDay | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [giving, setGiving] = useState(false);
  const [amount, setAmount] = useState("");
  // One id per give-back: a retry after a lost answer is the same return, never a second one.
  const [returnId, setReturnId] = useState(newRequestId);
  const [busy, setBusy] = useState(false);
  const supervisor = !readOnly && !holderUserId && /SUPERVISOR/.test(readRole() || "");
  const today = istToday();
  // The owner's view of a supervisor can go back through earlier days (?date=, never a future day).
  const asked = readOnly ? new URLSearchParams(search).get("date") || "" : "";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(asked) && asked < today ? asked : today;

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await getCashDay(date, holderUserId);
      if (res?.success && res.data) setDay(res.data);
      else setError(refusalText(res, t));
    } catch {
      setError(t("Couldn't load. Check the network."));
    }
  }, [holderUserId, date]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    load();
    if (supervisor) startPhotoQueue();
  }, [load, supervisor]);

  const reloadAll = () => {
    load();
    status.refresh();
  };

  const logout = async () => {
    if (!(await confirmUnsavedLogout())) return;
    logoutThisPhone();
    history.push(kp.login({ studio: auth?.organizationResponse?.name || "" }));
  };

  const giveBack = async () => {
    if (!amount || num(amount) <= 0) return;
    setBusy(true);
    try {
      const res: any = await cashReturn(undefined, { amount, clientRequestId: returnId });
      if (!res?.success) {
        notifications({ title: t("Not saved"), message: refusalText(res, t), type: "danger" });
        return;
      }
      setReturnId(newRequestId());
      setGiving(false);
      setAmount("");
      const next = await getCashDay(istToday()).catch(() => null);
      if (next?.success && next.data) setDay(next.data);
      notifications({ title: t("Saved"), message: t("Cash given back · Cash in hand {{amount}}", { amount: rupees(next?.data?.cash?.inHand ?? "") }), type: "success" });
      status.refresh();
    } catch {
      notifications({ title: t("No connection"), message: t("No network. Tap Save again; it won't be saved twice."), type: "warning" });
    } finally {
      setBusy(false);
    }
  };

  const changeAmount = (v: string) => {
    if (v !== amount) setReturnId(newRequestId());
    setAmount(v);
  };

  const name = readOnly ? day?.holder?.name || "" : auth?.name || "";

  const dayNav = (
    <div className="cb-row-btns cb-daynav">
      <Link to={`${pathname}?date=${shiftDay(date, -1)}`} className="cb-btn is-small" aria-label={t("Previous day")}>
        ‹ {t("Previous day")}
      </Link>
      <span className="cb-muted">{fmtShortDate(date, lang)}</span>
      {date < today ? (
        <Link to={shiftDay(date, 1) >= today ? pathname : `${pathname}?date=${shiftDay(date, 1)}`} className="cb-btn is-small" aria-label={t("Next day")}>
          {t("Next day")} ›
        </Link>
      ) : null}
    </div>
  );

  const header = readOnly ? (
    <>
      <h1 className="cb-title">{name}</h1>
      {dayNav}
    </>
  ) : (
    <div className="cb-who">
      <span className="cb-who__name">{name}</span>
      {supervisor ? (
        <button type="button" className="cb-link" onClick={logout}>
          {t("I am not {{name}}", { name })}
        </button>
      ) : null}
    </div>
  );

  if (error && !day)
    return (
      <div className="cb">
        {header}
        <div className="cb-error" role="alert">
          <span>{error}</span>
          <button type="button" className="kh-btn" onClick={load}>
            {t("Retry")}
          </button>
        </div>
      </div>
    );

  if (!day)
    return (
      <div className="cb" role="status" aria-label={t("Loading…")}>
        {header}
        <div className="cb-skeleton is-big" />
        <div className="cb-skeleton" />
        <div className="cb-skeleton" />
      </div>
    );

  const offline = (
    <>
      {pending > 0 ? <p className="cb-offline">{t("Saved on phone · {{n}} photos send when the network is back", { n: pending })}</p> : null}
      {supervisor ? <FailedPhotos /> : null}
    </>
  );

  // The three-step intro is for an empty first day only. A new supervisor may already hold cash or have a
  // handover waiting for "मिल गए" before he prints anything; hiding that would leave him no way to answer it.
  const hasMoney =
    day.transfers.incoming.length > 0 || day.transfers.outgoing.length > 0 || day.entries.length > 0 || num(day.cash.inHand) !== 0 || num(day.cash.incoming) !== 0;
  if (day.isFirstDay && !readOnly && !hasMoney)
    return (
      <div className="cb">
        {header}
        {offline}
        <h2 className="cb-title">{t("Cash")}</h2>
        <ol className="cb-steps">
          {(
            [
              ["printer", "Print the sheet"],
              ["sign", "Pay and get signatures"],
              ["camera", "Send a photo in the evening"],
            ] as const
          ).map(([icon, text], i) => (
            <li key={text} className="cb-step">
              <span className="cb-step__icon">
                <Svg name={icon} />
              </span>
              <span>
                <span className="cb-step__num">{i + 1}</span> {t(text)}
              </span>
            </li>
          ))}
        </ol>
        <Link to={kp.paySheets()} className="cb-btn is-primary">
          <Svg name="printer" />
          {t("Print pay sheet")}
        </Link>
        <p className="cb-muted" style={{ marginTop: 13 }}>
          {t("Couldn't print? Write the sheet number on top and send a photo of the handwritten page.")}
        </p>
        <Link to={kp.cashCamera({ kind: "PAY_SHEET" })} className="cb-btn is-small">
          <Svg name="camera" />
          {t("Send photo")}
        </Link>
        <p className="cb-muted" style={{ marginTop: 21 }}>
          {t("Photos keep your account clear. If something is wrong you can ask for a change.")}
        </p>
      </div>
    );

  const c = day.cash;
  const line = sumLine(c, t);
  const primary = readOnly ? null : primaryAction(day);
  const reminder = day.reminder ? reminderText(day.missing, today) : null;
  const incoming = day.transfers.incoming.filter((x) => x.state === "IN_TRANSIT");
  const missingFor = (s: SentItem) => (s.kind === "PAY_SHEET" && s.pages ? Array.from({ length: s.pages }, (_, i) => i + 1).filter((p) => !s.pagesSent.includes(p)) : []);

  return (
    <div className="cb">
      {header}
      {offline}
      <div className="cb-big">
        <div className="cb-big__label">{t("Cash in hand")}</div>
        <div className="cb-big__value" aria-live="polite">
          {rupees(c.inHand)}
        </div>
        {line ? <div className="cb-big__sum">{line}</div> : null}
        {incoming.map((x) => (
          <div key={x.id} className="cb-big__incoming">
            {t('Coming: {{amount}} (from {{name}}, adds when you tap "Received")', { amount: rupees(x.amount), name: x.from.name })}
          </div>
        ))}
      </div>

      {readOnly && day.cleared?.length
        ? day.cleared.map((c) => (
            <div key={`${c.kind}-${c.date}`} className="cb-card">
              <p>
                {t("Paid by hand, no sheet photo. Reason by {{name}}: {{reason}}", { name: c.clearedBy.name, reason: c.reason })}
              </p>
            </div>
          ))
        : null}

      {reminder ? (
        <div className="cb-card is-warn" role="status">
          <p>{t(reminder)}</p>
        </div>
      ) : null}

      {primary ? (
        <Link to={primary.to} className="cb-btn is-primary">
          <Svg name={day.primary === "PRINT_SHEET" ? "printer" : "camera"} />
          {t(primary.label)}
        </Link>
      ) : day.sent.length && !readOnly ? (
        <p className="cb-card is-ok">{t("Today's photos sent")}</p>
      ) : null}

      <TransferCards incoming={day.transfers.incoming} outgoing={day.transfers.outgoing} onChanged={reloadAll} readOnly={readOnly} />

      {!readOnly ? (
        <div className="cb-row-btns">
          <Link to={kp.cashGive()} className="cb-btn">
            <Svg name="give" />
            {t("Hand cash to…")}
          </Link>
          <button type="button" className="cb-btn" onClick={() => setGiving((g) => !g)} aria-expanded={giving}>
            <Svg name="back" />
            {t("Give cash back")}
          </button>
          <Link to={kp.cashSpend()} className="cb-btn">
            <Svg name="spend" />
            {t("Type a spend")}
          </Link>
        </div>
      ) : null}

      {giving ? (
        <div className="cb-card">
          <Keypad value={amount} onChange={changeAmount} label={t("Amount")} id="cb-return" />
          {num(amount) > num(c.inHand) ? <p className="cb-muted">{t("You seem to have only {{amount}}", { amount: rupees(c.inHand) })}</p> : null}
          <button type="button" className="cb-btn is-primary" disabled={busy || num(amount) <= 0} onClick={giveBack}>
            {busy ? t("Saving…") : t("Save {{amount}}", { amount: rupees(amount || 0) })}
          </button>
        </div>
      ) : null}

      {day.sent.length ? (
        <>
          <h3 className="cb-section">{t("Sent ✓")}</h3>
          <ul className="cb-list">
            {day.sent.map((s, i) => {
              const left = missingFor(s);
              const title = s.kind === "PAY_SHEET" ? [s.sheetNo, s.people !== null ? t("{{n}} people", { n: s.people }) : null].filter(Boolean).join(" · ") : t("Spends page · {{date}}", { date: fmtShortDate(day.date, lang) });
              return (
                <li key={`${s.printId || s.kind}-${i}`}>
                  {left.length && s.printId && !readOnly ? (
                    <Link to={kp.cashCamera({ kind: "PAY_SHEET", printId: s.printId, page: left[0] })} className="cb-item">
                      <span className="cb-item__main">
                        {title}
                        <span className="cb-item__sub cb-state is-warn">{t("Pages {{pages}} not sent", { pages: pageList(left) })}</span>
                      </span>
                      <span className="cb-state is-sent">
                        {t(STATE_WORD[s.state])} {fmtTime(s.sentAt)}
                      </span>
                    </Link>
                  ) : (
                    <div className="cb-item">
                      <span className="cb-item__main">
                        {title}
                        {left.length ? <span className="cb-item__sub cb-state is-warn">{t("Pages {{pages}} not sent", { pages: pageList(left) })}</span> : null}
                      </span>
                      <span className={`cb-state ${s.state === "CHECKED" ? "is-ok" : "is-sent"}`}>
                        {t(STATE_WORD[s.state])} {s.state === "CHECKED" ? "" : fmtTime(s.sentAt)}
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}

      {day.entries.length || day.rows.length ? (
        <>
          <h3 className="cb-section">{t("Recent")}</h3>
          <ul className="cb-list">
            {day.entries.map((e) => (
              <li key={`${e.recordType}-${e.id}`}>
                <Link to={kp.cashEntry(e.recordType, e.id)} className="cb-item">
                  <span className="cb-item__main">
                    {entryTitle(e, t)}
                    <span className={`cb-item__sub cb-state ${entryState(e) === "CHECKED" ? "is-ok" : "is-sent"}`}>{t(STATE_WORD[entryState(e)])}</span>
                  </span>
                  <span className="cb-item__amt">{rupees(e.amount)}</span>
                </Link>
              </li>
            ))}
            {day.rows.map((r) => (
              <li key={r.rowId}>
                <Link to={kp.cashEntry("CASH_ROW", r.rowId)} className="cb-item">
                  <span className="cb-item__main">
                    {r.personName}
                    <span className="cb-item__sub">
                      {r.sheetNo} · <span className={`cb-state ${r.state === "CHECKED" ? "is-ok" : "is-sent"}`}>{t(STATE_WORD[r.state])}</span>
                    </span>
                  </span>
                  <span className="cb-item__amt">{r.paid === null ? "—" : rupees(r.paid)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {error ? (
        <div className="cb-error" role="alert">
          <span>{error}</span>
          <button type="button" className="kh-btn" onClick={load}>
            {t("Retry")}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default SupervisorCashHome;
